import Image, { ImageProps } from "next/image";
import { useEffect } from "react";
import { Box, BoxProps, Divider, Stack, StackProps, Typography, TypographyProps, useMediaQuery } from "@mui/material";
import { API_URL } from "helpers/common";
import { PageProps } from "pages/_app";
import intuitIcon from "assets/icons/landing-page/intuit.svg";
import AppLink from "components/v2/atoms/app-link/AppLink";
import AppIconSocialButton, {
    AppIconSocialButtonProps,
} from "components/v2/atoms/app-social-button/AppIconSocialButton";
import trustIcons from "assets/icons/landing-page/quickbooks_trusticons.png";
import trustIconsDesktop from "assets/icons/landing-page/quickbooks_trusticons.png";
import AppIconButton, { AppIconButtonProps } from "components/v2/atoms/app-icon-button/AppIconButton";
import AppIntuitButton, { AppIntuitButtonProps } from "components/v2/templates/instant-vat/others/AppIntuitButton";
import { InstantVatAPI } from "apis/v2/instant-vat";
import saleBannerImage from "assets/icons/sale-banner-green.svg";
import { usePartnerId } from "hooks/partner-id";
import { useRouter } from "next/router";

interface RightPanelProps {
    isMobile: boolean;
    adsum_fees: string;
    partner: string;
    companyId: string;
}

interface LeftPanelProps {
    isMobile: boolean;
    partner: string;
    companyId: string;
}

interface ContentProps {
    partner: string;
    fees: string;
    companyId: string;
}

function Quickbooks(props: PageProps) {
    const companyId = props.companyId;
    const router = useRouter();
    const { partner } = usePartnerId({ defaultPartner: "intuit" });
    const fees = "15";

    const methods = {
        async sendActivity(companyId: string) {
            try {
                const company_id = companyId;
                const activity = "viewed Quickbooks landing page";
                InstantVatAPI.sendHelpPageAlert(company_id, activity);
            } catch (error) {
                console.log(`Error`, error);
            }
        },
    };

    useEffect(() => {
        if (partner) {
            methods.sendActivity(companyId || "");
        }

        router.push(`/v2/instant-vat?partner_id=${partner}`);
    }, [partner]);

    const contentProps: ContentProps = {
        partner: partner,
        fees: fees,
        companyId: companyId || "",
    };

    return <Content {...contentProps} />;
}

function Content(props: ContentProps) {
    const isMobile = useMediaQuery("(max-width:1040px)");

    const outerContainerProps: BoxProps = {
        sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: isMobile ? 0 : "30px",
            boxSizing: "border-box",
            minHeight: isMobile ? "100%" : "80vh",
            height: "auto",
            maxHeight: "auto",
            padding: isMobile ? 2 : "auto",
            minWidth: isMobile ? "375px" : "1044px",
            maxWidth: isMobile ? "650px" : "1044px",
            margin: "auto",
        },
    };

    const stackProps: StackProps = {
        sx: {
            flexDirection: isMobile ? "column" : "row",
        },
        gap: 3,
    };

    const leftStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: isMobile ? "auto" : "550px",
        },
        position: "relative",
        border: 0,
        borderRadius: 1,
        boxShadow: 0,
        maxWidth: isMobile ? "615px" : "560px",
    };

    const rightStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: "auto",
            marginBottom: "auto",
        },
        border: 0,
        borderRadius: 1,
        boxShadow: 0,
        maxWidth: isMobile ? "615px" : "450px",
    };

    const rightPanelProps: RightPanelProps = {
        isMobile: isMobile,
        adsum_fees: props.fees,
        partner: props.partner,
        companyId: props.companyId,
    };

    const leftPanelProps: LeftPanelProps = {
        isMobile: isMobile,
        partner: props.partner,
        companyId: props.companyId,
    };

    const trustImageStackProps: StackProps = {
        children: (
            <Image
                src={trustIcons}
                alt="tpreview"
                style={{
                    maxWidth: "100%",
                    height: "auto",
                }}
            />
        ),
        sx: {
            alignItems: "center",
            paddingRight: 4,
            paddingLeft: 4,
            paddingTop: 0,
            paddingBottom: 0,
        },
    };

    const trustImageDesktopStackProps: StackProps = {
        children: (
            <Image
                src={trustIconsDesktop}
                alt="tpreview"
                width={"437"}
                height={"64"}
                style={{
                    maxWidth: "100%",
                    height: "auto",
                }}
            />
        ),
        sx: {
            alignItems: "center",
            paddingRight: 4,
            paddingLeft: 4,
            paddingTop: 4,
        },
    };

    const footerProps: TypographyProps = {
        sx: {
            mt: { xs: -2, md: 3 },
            p: 2,
            textAlign: "center",
        },
        color: "#9DA4AF",
        fontSize: { xs: 11, md: 11 },
        children: "As part of our partnership we'll keep Intuit updated if you move forward with InstantVAT",
    };

    const saleBannerImageProps: ImageProps = {
        src: saleBannerImage,
        alt: "Sale Banner",
        style: {
            maxWidth: isMobile ? "60px" : "150px",
            height: "auto",
            position: "absolute",
            top: isMobile ? -5 : -20,
            right: isMobile ? 0 : -10,
        },
    };

    return (
        <Box {...outerContainerProps}>
            <Stack>
                <Stack {...stackProps}>
                    <Stack {...leftStackProps}>
                        <LeftPanel {...leftPanelProps} />
                        <Image {...saleBannerImageProps} alt="Sale Banner" />
                    </Stack>
                    {isMobile && <Stack {...trustImageStackProps}></Stack>}
                    <Stack {...rightStackProps}>
                        <RightPanel {...rightPanelProps} />
                    </Stack>
                </Stack>
                {!isMobile && (
                    <>
                        <Stack {...trustImageDesktopStackProps}></Stack>
                        <Typography {...footerProps}></Typography>
                    </>
                )}
            </Stack>
        </Box>
    );
}

export default Quickbooks;

function LeftPanel(props: LeftPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            p: 3,
            alignContent: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "flex-start" },
            textAlign: "left",
        },
        gap: 2,
    };

    const loginLinkProps: TypographyProps = {
        fontSize: 12,
        color: "text.disabled",
        sx: {
            textAlign: { xs: "center", md: "left" },
            mb: { xs: 0, md: 2 },
        },
    };

    const buttonStackProps: StackProps = {
        sx: {
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mb: 1,
        },
        gap: { xs: 1, md: 2 },
    };

    const googleButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_google",
        brand: "google",
        onClick() {
            let url = API_URL + "/google_signup";
            if (props.partner) {
                url += `?partner_signup=${props.partner}`;
            } else {
                url += "?partner_signup=intuit";
            }

            location.href = url;
        },
    };

    const microsoftButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_microsoft",
        brand: "microsoft",
        onClick() {
            let url = API_URL + "/microsoft_signup";
            if (props.partner) {
                url += `?partner_signup=${props.partner}`;
            } else {
                url += "?partner_signup=intuit";
            }

            location.href = url;
        },
    };

    const footerProps: TypographyProps = {
        sx: {
            mt: { xs: -2, md: "auto" },
            p: 2,
            textAlign: { xs: "center", md: "left" },
        },
        color: "#9DA4AF",
        fontSize: { xs: 8, md: 10 },
    };

    const emailButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_email",
        brand: "email",
        onClick() {
            let url = API_URL + "/portal/v2/instant-vat/steps/email";
            if (props.partner) {
                url += `?partner_id=${props.partner}`;
            } else {
                url += "?partner_id=intuit";
            }
            location.href = url;
        },
    };

    const intuitButtonProps: AppIntuitButtonProps = {
        id: "intuit_signup",
        onClick() {
            location.href = API_URL + "/intuit_signup";
        },
        isFilled: true,
    };

    const headerStackProps: StackProps = {
        sx: {
            p: 3,
            flexDirection: "row",
            alignContent: "center",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
        },
    };

    const newStackProps: StackProps = {
        sx: {
            backgroundColor: "#32373E",
            borderRadius: 0.5,
            color: "white",
            paddingX: 1,
            paddingY: 0,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
            mr: 1,
        },
    };

    const newTypographyProps: TypographyProps = {
        fontSize: { xs: 12, md: 12 },
        fontWeight: 500,
        margin: 0,
        lineHeight: 1,
        paddingX: 0.5,
        paddingY: 0.5,
    };

    const headingProps: TypographyProps = {
        fontSize: { xs: 14, md: 18 },
        fontWeight: 500,
        mr: 0.5,
        textAlign: "center",
    };

    const disabledTextProps: TypographyProps = {
        fontSize: { xs: 14, md: 14 },
        color: "#9DA4AF",
    };

    const setupTextProps: TypographyProps = {
        color: "#0D333F",
        fontSize: 14,
        fontWeight: 500,
        mt: 1.5,
        textAlign: {
            xs: "center",
            md: "left",
        },
    };

    return (
        <Stack {...stackProps}>
            <Stack {...headerStackProps}>
                <Stack {...newStackProps}>
                    <Typography {...newTypographyProps}>NEW</Typography>
                </Stack>
                <Typography {...headingProps}>InstantVAT for</Typography>
                <Box
                    sx={{
                        width: {
                            xs: "80px",
                            md: "120px",
                        },
                        height: {
                            xs: "20px",
                            md: "30px",
                        },
                    }}>
                    <Image
                        src={intuitIcon}
                        alt="intuitIcon"
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                        }}
                    />
                </Box>
            </Stack>
            <Divider sx={{ borderColor: "#E3E6EB" }} flexItem />
            <Stack {...bodyStackProps}>
                <Typography sx={{ fontSize: { xs: 16, md: 16 } }} mb={-1}>
                    Congratulations! You are pre-approved for InstantVAT
                </Typography>
                <Typography variant="h1" color="#32373E">
                    Get your VAT refund paid
                    {props.isMobile ? " " : <br />}
                    in as little as 24 hours.
                </Typography>
                <Typography {...disabledTextProps}>Submit your VAT return as normal, get paid faster.</Typography>
                <Typography {...setupTextProps}>Set up in under 4 minutes.</Typography>
                <Stack {...buttonStackProps}>
                    <AppIntuitButton {...intuitButtonProps} />
                    <Typography sx={{ fontSize: { xs: 14, md: 14 } }}>or</Typography>
                    <Stack flexDirection={"row"} gap={2}>
                        <AppIconSocialButton {...googleButtonProps} />
                        <AppIconSocialButton {...microsoftButtonProps} />
                        <AppIconSocialButton {...emailButtonProps} />
                    </Stack>
                </Stack>
                <Typography {...loginLinkProps}>
                    Already have an account?{" "}
                    <AppLink color={"#2CA01C"} id="login-link" href={API_URL + "/portal/v2/auth/login"}>
                        Log in
                    </AppLink>
                </Typography>
            </Stack>
            {props.isMobile && (
                <Typography {...footerProps}>
                    As part of our partnership we&apos;ll keep Intuit updated if you move forward with InstantVAT
                    <sup>®</sup>.
                </Typography>
            )}
        </Stack>
    );
}

function RightPanel(props: RightPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            p: 3,
            py: 0,
            mb: { xs: 0, md: 2 },
        },
        gap: 2,
    };

    const headerStackProps: StackProps = {
        sx: {
            p: 3,
            flexDirection: "row",
            justifyContent: "space-between",
        },
    };

    const helpStackProps: TypographyProps = {
        sx: {
            display: "flex",
            alignItems: "right",
            justifyContent: "right",
            alignSelf: "right",
            gap: 0,
        },
    };

    const methods = {
        async sendActivity(companyId: string) {
            try {
                const company_id = companyId;
                const activity = "clicked on Help - redirected to knowledge base";
                InstantVatAPI.sendHelpPageAlert(company_id, activity);
            } catch (error) {
                console.log(`Error`, error);
            }
        },
    };

    const helpHeadingProps: TypographyProps = {
        fontSize: { xs: 16, md: 16 },
        color: "#32373E",
        fontWeight: 500,
        sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0,
            cursor: "pointer",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        onClick: () => {
            methods.sendActivity(props.companyId || "");
            window.open("https://mtd.adsum-works.com/knowledge", "_blank");
        },
    };

    const hintButtonProps: AppIconButtonProps = {
        sx: {
            color: "#32373E",
        },
        onClick: () => {
            methods.sendActivity(props.companyId || "");
            window.open("https://mtd.adsum-works.com/knowledge", "_blank");
        },
    };

    const disabledTextProps: TypographyProps = {
        fontSize: { xs: 14, md: 14 },
        color: "#9DA4AF",
        mt: 2,
    };

    return (
        <Stack {...stackProps}>
            <Stack {...headerStackProps}>
                <Typography sx={{ fontSize: { xs: 18, md: 18 } }} color={"#0D333F"} fontWeight={500}>
                    Why choose InstantVAT?
                </Typography>
                <Typography {...helpStackProps}>
                    <AppIconButton {...hintButtonProps} />
                    <Typography {...helpHeadingProps}> Help </Typography>
                </Typography>
            </Stack>
            <Divider sx={{ borderColor: "#E3E6EB" }} flexItem />
            <Stack {...bodyStackProps}>
                <Typography sx={{ fontSize: { xs: 14, md: 14 } }} lineHeight={2}>
                    <ul className="accelerated-dialog-list">
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>Improve your cash flow</span> -
                                Boost your business operations. Save an average of 47 days waiting for HMRC to pay your
                                VAT refunds every year.
                            </Typography>
                        </li>
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>Pre-approved</span> - No application
                                or form-filling necessary. Your business has been pre-approved for InstantVAT.
                            </Typography>
                        </li>
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>Business as usual</span> - No
                                process changes. Just submit your VAT return as usual and get paid faster.
                            </Typography>
                        </li>
                        <li>
                            {props.adsum_fees === "0" ? (
                                <Typography {...disabledTextProps}>
                                    <span
                                        style={{
                                            fontWeight: 500,
                                            color: "#32373E",
                                        }}>
                                        30 days completely free
                                    </span>{" "}
                                    then just 2% per refund, no commitment or subscription required.
                                </Typography>
                            ) : (
                                <Typography {...disabledTextProps}>
                                    <span style={{ fontWeight: 500, color: "#32373E" }}>From just £5 per month</span>{" "}
                                    +0.99% added to transactions over £1,000.
                                </Typography>
                            )}
                        </li>
                    </ul>
                </Typography>
            </Stack>
        </Stack>
    );
}
